import React, { useState, useEffect } from 'react';
import { exportAPI, usersAPI, downloadFile } from '../services/api';
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  DocumentChartBarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';
import { toast } from 'react-toastify';

function Reports() {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filters for reports
  const [filters, setFilters] = useState({
    startDate: moment().startOf('month').toDate(),
    endDate: moment().endOf('month').toDate(),
    userId: '',
    department: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [usersResponse, departmentsResponse] = await Promise.all([
        usersAPI.getUsers({ limit: 1000 }),
        usersAPI.getDepartmentsWithCount()
      ]);
      
      setUsers(usersResponse.data.users || []);
      // Extract department names from the response
      const departmentNames = departmentsResponse.data?.departments?.map(dept => dept.name) || [];
      setDepartments(departmentNames);
    } catch (err) {
      console.error('Error loading initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExport = async (type, format) => {
    try {
      setExporting(true);
      const params = {
        startDate: moment(filters.startDate).format('YYYY-MM-DD'),
        endDate: moment(filters.endDate).format('YYYY-MM-DD'),
        userId: filters.userId,
        department: filters.department
      };

      let response;
      let filename;
      const dateStr = moment().format('YYYY-MM-DD');

      if (type === 'attendance') {
        if (format === 'csv') {
          response = await exportAPI.exportCSV(params);
          filename = `attendance_report_${dateStr}.csv`;
        } else if (format === 'excel') {
          response = await exportAPI.exportExcel(params);
          filename = `attendance_report_${dateStr}.xlsx`;
        }
      } else if (type === 'daily-hours') {
        response = await exportAPI.exportDailyHoursExcel(params);
        filename = `daily_hours_report_${dateStr}.xlsx`;
      }

      downloadFile(response.data, filename);
      toast.success(`Report exported successfully as ${format.toUpperCase()}!`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const reportTypes = [
    {
      id: 'attendance',
      title: 'Attendance Logs Report',
      description: 'Detailed attendance logs with check-in/check-out times',
      icon: ClockIcon,
      color: 'bg-blue-500',
      formats: ['csv', 'excel']
    },
    {
      id: 'daily-hours',
      title: 'Daily Working Hours Report',
      description: 'Summary of daily working hours for each employee',
      icon: CalendarDaysIcon,
      color: 'bg-green-500',
      formats: ['excel']
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
        <p className="text-gray-600">Generate and export attendance reports</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Report Filters</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Range */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
              <DatePicker
                selected={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                selectsStart
                startDate={filters.startDate}
                endDate={filters.endDate}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholderText="Start date"
              />
              <DatePicker
                selected={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                selectsEnd
                startDate={filters.startDate}
                endDate={filters.endDate}
                minDate={filters.startDate}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholderText="End date"
              />
            </div>
          </div>

          {/* User Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
            <select
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="">All Users</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Department Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <select
              value={filters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Report Types */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reportTypes.map((report) => (
          <div key={report.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className={`${report.color} p-3 rounded-md`}>
                  <report.icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">{report.title}</h3>
                  <p className="text-sm text-gray-500">{report.description}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-sm text-gray-700">
                  <strong>Date Range:</strong> {moment(filters.startDate).format('MMM DD, YYYY')} - {moment(filters.endDate).format('MMM DD, YYYY')}
                </div>
                
                {filters.userId && (
                  <div className="text-sm text-gray-700">
                    <strong>User:</strong> {users.find(u => u.id === filters.userId)?.firstName} {users.find(u => u.id === filters.userId)?.lastName}
                  </div>
                )}
                
                {filters.department && (
                  <div className="text-sm text-gray-700">
                    <strong>Department:</strong> {filters.department}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mt-4">
                  {report.formats.map((format) => (
                    <button
                      key={format}
                      onClick={() => handleExport(report.id, format)}
                      disabled={exporting}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                      Export {format.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => {
                setFilters({
                  startDate: moment().startOf('day').toDate(),
                  endDate: moment().endOf('day').toDate(),
                  userId: '',
                  department: ''
                });
              }}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <CalendarDaysIcon className="h-5 w-5 mr-2 text-gray-400" />
              Today's Report
            </button>

            <button
              onClick={() => {
                setFilters({
                  startDate: moment().startOf('week').toDate(),
                  endDate: moment().endOf('week').toDate(),
                  userId: '',
                  department: ''
                });
              }}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <CalendarDaysIcon className="h-5 w-5 mr-2 text-gray-400" />
              This Week
            </button>

            <button
              onClick={() => {
                setFilters({
                  startDate: moment().startOf('month').toDate(),
                  endDate: moment().endOf('month').toDate(),
                  userId: '',
                  department: ''
                });
              }}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <CalendarDaysIcon className="h-5 w-5 mr-2 text-gray-400" />
              This Month
            </button>

            <button
              onClick={() => {
                setFilters({
                  startDate: moment().subtract(1, 'month').startOf('month').toDate(),
                  endDate: moment().subtract(1, 'month').endOf('month').toDate(),
                  userId: '',
                  department: ''
                });
              }}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <CalendarDaysIcon className="h-5 w-5 mr-2 text-gray-400" />
              Last Month
            </button>
          </div>
        </div>
      </div>

      {/* Export Status */}
      {exporting && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-center">
            <div className="spinner mr-3"></div>
            <div className="text-blue-800">
              <strong>Generating report...</strong>
              <p className="text-sm">This may take a few moments depending on the data size.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Reports;
